/**
 * Geocoding client for the onboarding map picker.
 *
 * Talks to the Partner Connect backend `/geo/*` endpoints, which proxy Google
 * Places API (New) + Geocoding API. The Google API key never leaves the
 * server — the frontend just hits our own backend with a session token.
 *
 * A "session" begins when the map modal opens (we generate a UUID) and ends
 * when the user picks a result (which triggers `getPlaceDetails`). Google
 * bills the chain of autocomplete calls + final details call as one session
 * when the same token is passed, so always reuse it for the lifetime of a
 * single picker session.
 */

import { apiClient } from 'shared/lib/apiClient';

export type SupportedGeoLang = 'he' | 'ar' | 'en';

export interface Prediction {
  placeId: string;
  /** "Allenby 12" — what we show bold in the dropdown row. */
  mainText: string;
  /** "Tel Aviv-Yafo, Israel" — what we show in muted secondary text. */
  secondaryText: string;
  /** Full human-readable text, used to fill the input on selection. */
  fullText: string;
}

export interface PlaceDetails {
  placeId: string;
  lat: number;
  lng: number;
  formattedAddress: string;
  city: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  country: string;
}

/**
 * Generate a session token. Uses `crypto.randomUUID` when available, falls
 * back to a v4-shaped string built from `Math.random` on older browsers.
 */
export const newSessionToken = (): string => {
  const c = typeof crypto !== 'undefined' ? (crypto as Crypto) : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // RFC4122-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

interface AutocompleteResponse {
  predictions: Prediction[];
}

export async function autocompleteAddress(
  q: string,
  sessionToken: string,
  signal?: AbortSignal,
  lang: SupportedGeoLang = 'he',
): Promise<Prediction[]> {
  const trimmed = q?.trim();
  if (!trimmed) return [];

  const res = await apiClient.get<AutocompleteResponse>('/geo/autocomplete', {
    params: { q: trimmed, sessionToken, lang },
    signal,
  });
  return res.data?.predictions ?? [];
}

export async function getPlaceDetails(
  placeId: string,
  sessionToken: string,
  signal?: AbortSignal,
  lang: SupportedGeoLang = 'he',
): Promise<PlaceDetails | null> {
  if (!placeId) return null;
  const res = await apiClient.get<PlaceDetails>(
    `/geo/place/${encodeURIComponent(placeId)}`,
    { params: { sessionToken, lang }, signal },
  );
  const d = res.data;
  if (!d || Number.isNaN(d.lat) || Number.isNaN(d.lng)) return null;
  return d;
}

export async function reverseGeocode(
  lat: number,
  lng: number,
  signal?: AbortSignal,
  lang: SupportedGeoLang = 'he',
): Promise<PlaceDetails | null> {
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  const res = await apiClient.get<PlaceDetails>('/geo/reverse', {
    params: { lat, lng, lang },
    signal,
  });
  return res.data ?? null;
}
